(function () {
  const DESKTOP_MEDIA_QUERY = "(min-width: 1025px)";

  class ExpandableCard extends HTMLElement {
    constructor() {
      super();
      this.openHoverState = this.openHoverState.bind(this);
      this.closeHoverState = this.closeHoverState.bind(this);
      this.toggleClickState = this.toggleClickState.bind(this);
      this.onCardClick = this.onCardClick.bind(this);
      this.onInteractionModeChange = this.onInteractionModeChange.bind(this);
      this.hoverEnabled = false;
      this.clickEnabled = false;
    }

    connectedCallback() {
      this.trigger = this.querySelector("[data-expandable-toggle], .expandable-card__toggle");
      if (!this.trigger) return;

      // Drawer mode needs theme modal modules — fall back to click toggle.
      if (this.dataset.behavior === "drawer") {
        this.enableClickInteraction();
        return;
      }

      this.desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
      if (this.desktopMediaQuery.addEventListener) {
        this.desktopMediaQuery.addEventListener("change", this.onInteractionModeChange);
      } else {
        this.desktopMediaQuery.addListener(this.onInteractionModeChange);
      }
      this.onInteractionModeChange();
    }

    disconnectedCallback() {
      if (this.trigger) {
        this.trigger.removeEventListener("click", this.toggleClickState);
      }
      if (this.desktopMediaQuery) {
        if (this.desktopMediaQuery.removeEventListener) {
          this.desktopMediaQuery.removeEventListener("change", this.onInteractionModeChange);
        } else {
          this.desktopMediaQuery.removeListener(this.onInteractionModeChange);
        }
      }
      this.disableHoverInteraction();
      this.disableClickInteraction();
    }

    onInteractionModeChange() {
      if (this.desktopMediaQuery.matches) {
        this.enableHoverInteraction();
        this.disableClickInteraction();
      } else {
        this.disableHoverInteraction();
        this.enableClickInteraction();
      }
    }

    enableHoverInteraction() {
      if (this.hoverEnabled) return;
      this.hoverEnabled = true;
      this.addEventListener("pointerenter", this.openHoverState);
      this.addEventListener("pointerleave", this.closeHoverState);
      this.addEventListener("focusin", this.openHoverState);
      this.addEventListener("focusout", this.closeHoverState);
    }

    disableHoverInteraction() {
      if (!this.hoverEnabled) return;
      this.hoverEnabled = false;
      this.removeEventListener("pointerenter", this.openHoverState);
      this.removeEventListener("pointerleave", this.closeHoverState);
      this.removeEventListener("focusin", this.openHoverState);
      this.removeEventListener("focusout", this.closeHoverState);
      this.classList.remove("detail-open");
      this.trigger?.setAttribute("aria-expanded", "false");
    }

    enableClickInteraction() {
      if (this.clickEnabled || !this.trigger) return;
      this.clickEnabled = true;
      this._pointerStartX = 0;
      this._onPointerDown = (event) => {
        this._pointerStartX = event.clientX || 0;
      };
      this.addEventListener("pointerdown", this._onPointerDown, { passive: true });
      this.trigger.addEventListener("click", this.toggleClickState);
      this.addEventListener("click", this.onCardClick);
    }

    disableClickInteraction() {
      if (!this.clickEnabled || !this.trigger) return;
      this.clickEnabled = false;
      this.trigger.removeEventListener("click", this.toggleClickState);
      this.removeEventListener("click", this.onCardClick);
      if (this._onPointerDown) {
        this.removeEventListener("pointerdown", this._onPointerDown);
        this._onPointerDown = null;
      }
      this.classList.remove("detail-open");
      this.trigger.setAttribute("aria-expanded", "false");
    }

    onCardClick(event) {
      if (event.target.closest("a, button")) return;
      // Ignore click that follows a horizontal swipe on the carousel
      if (Math.abs((event.clientX || 0) - (this._pointerStartX || 0)) > 12) return;
      this.toggleClickState(event);
    }

    openHoverState() {
      this.classList.add("detail-open");
      this.trigger?.setAttribute("aria-expanded", "true");
    }

    closeHoverState(event) {
      if (event.type === "focusout" && this.contains(event.relatedTarget)) return;
      this.classList.remove("detail-open");
      this.trigger?.setAttribute("aria-expanded", "false");
    }

    toggleClickState(event) {
      event.preventDefault();
      event.stopPropagation();
      const willOpen = !this.classList.contains("detail-open");
      if (willOpen) {
        // Accordion: only one open card at a time on touch
        this.closest(".expandable-card-section__grid")
          ?.querySelectorAll("expandable-card.detail-open")
          ?.forEach((card) => {
            if (card === this) return;
            card.classList.remove("detail-open");
            card.querySelector("[data-expandable-toggle]")?.setAttribute("aria-expanded", "false");
          });
      }
      const isOpen = this.classList.toggle("detail-open");
      this.trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
    }
  }

  if (!customElements.get("expandable-card")) {
    customElements.define("expandable-card", ExpandableCard);
  }

  function initExpandableCarousels(root) {
    const tracks = (root || document).querySelectorAll("[data-expandable-track].expandable-card-section__track--carousel");
    tracks.forEach((track) => {
      if (track.dataset.carouselReady === "true") return;
      const scroller = track.querySelector("[data-expandable-scroller]");
      const prev = track.querySelector("[data-expandable-prev]");
      const next = track.querySelector("[data-expandable-next]");
      if (!scroller || !prev || !next) return;

      track.dataset.carouselReady = "true";

      const getStep = () => {
        const card = scroller.querySelector(".expandable-card, .shopify-block");
        if (!card) return scroller.clientWidth * 0.7;
        const styles = window.getComputedStyle(scroller);
        const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
        return card.getBoundingClientRect().width + gap;
      };

      const getMaxScroll = () =>
        Math.max(0, scroller.scrollWidth - scroller.clientWidth);

      const updateNav = () => {
        const maxScroll = getMaxScroll();
        prev.disabled = scroller.scrollLeft <= 2;
        next.disabled = scroller.scrollLeft >= maxScroll - 2;
      };

      // Smooth + mandatory snap can snap back to the start; disable snap while we move.
      let snapRestoreTimer = 0;
      const scrollToLeft = (left) => {
        window.clearTimeout(snapRestoreTimer);
        scroller.style.scrollSnapType = "none";
        scroller.scrollTo({ left, behavior: "smooth" });
        snapRestoreTimer = window.setTimeout(() => {
          scroller.style.scrollSnapType = "";
          updateNav();
        }, 450);
      };

      const scrollByDir = (dir) => {
        const maxScroll = getMaxScroll();
        const step = getStep();

        if (dir > 0) {
          const target = scroller.scrollLeft + step;
          scrollToLeft(target >= maxScroll - step * 0.35 ? maxScroll : target);
          return;
        }

        const target = scroller.scrollLeft - step;
        scrollToLeft(target <= step * 0.35 ? 0 : target);
      };

      const onPrev = () => scrollByDir(-1);
      const onNext = () => scrollByDir(1);

      prev.addEventListener("click", onPrev);
      next.addEventListener("click", onNext);
      scroller.addEventListener("scroll", updateNav, { passive: true });
      window.addEventListener("resize", updateNav);

      track._expandableCarouselCleanup = () => {
        window.clearTimeout(snapRestoreTimer);
        scroller.style.scrollSnapType = "";
        prev.removeEventListener("click", onPrev);
        next.removeEventListener("click", onNext);
        scroller.removeEventListener("scroll", updateNav);
        window.removeEventListener("resize", updateNav);
        delete track.dataset.carouselReady;
        delete track._expandableCarouselCleanup;
      };

      updateNav();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initExpandableCarousels());
  } else {
    initExpandableCarousels();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initExpandableCarousels(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target;
    if (!root || typeof root.querySelectorAll !== "function") return;
    const tracks = root.matches?.("[data-expandable-track]")
      ? [root]
      : [...root.querySelectorAll("[data-expandable-track]")];
    tracks.forEach((track) => track._expandableCarouselCleanup?.());
  });
})();
